'use client'

import { type ChangeEvent, useState } from 'react'
import { useLocale } from '~/contexts/LocaleContext'
import IcPlus from '~/icons/ic_plus'
import IcSubtract from '~/icons/ic_subtract'
import { getTranslation } from '~/lib/get-translation'

interface StepperControlProps {
  value: number
  min: number
  max: number
  onDecrement: () => void
  onIncrement: () => void
  onChange: (value: number) => void
}

export default function StepperControl({
  value,
  min,
  max,
  onDecrement,
  onIncrement,
  onChange,
}: StepperControlProps) {
  const [isDecrementActive, setIsDecrementActive] = useState(false)
  const [isDecrementHovered, setIsDecrementHovered] = useState(false)
  const [isIncrementActive, setIsIncrementActive] = useState(false)
  const [isIncrementHovered, setIsIncrementHovered] = useState(false)

  const sanitizeInput = (input: string): string => {
    // Thay tất cả dấu phẩy thành dấu chấm
    let cleaned = input.replace(/,/g, '.')

    // Tìm chuỗi số hợp lệ liên tiếp đầu tiên (có thể có dấu trừ ở đầu, có thể có dấu chấm)
    const match = cleaned.match(/^-?\d*\.?\d*/)
    if (match) {
      cleaned = match[0]
    } else {
      cleaned = ''
    }
    return cleaned
  }

  const clampValue = (value: number): number => {
    return Math.min(Math.max(value, min), max)
  }

  const parseAndValidateInput = (input: string): number | null => {
    if (input === '') return null
    const parsed = parseFloat(input)
    return isNaN(parsed) ? null : parsed
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const sanitizedInput = sanitizeInput(e.target.value)
    if (sanitizedInput === '') {
      onChange?.(min)
      return
    }

    const parsedValue = parseAndValidateInput(sanitizedInput)
    if (parsedValue !== null) {
      onChange?.(clampValue(parsedValue))
    }
  }

  const handleDecrement = () => {
    if (value > min) {
      onDecrement()
    }
  }

  const handleIncrement = () => {
    if (value < max) {
      onIncrement()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent negative signs and e (scientific notation)
    if (e.key === '-' || e.key === 'e') {
      e.preventDefault()
    }

    // Handle up/down arrow keys
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      handleIncrement()
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      handleDecrement()
    }
  }

  const handleButtonMouseEvents = {
    decrement: {
      onMouseDown: () => setIsDecrementActive(true),
      onMouseUp: () => setIsDecrementActive(false),
      onMouseLeave: () => {
        setIsDecrementActive(false)
        setIsDecrementHovered(false)
      },
      onMouseEnter: () => setIsDecrementHovered(true),
      onTouchStart: () => setIsDecrementActive(true),
      onTouchEnd: () => setIsDecrementActive(false),
    },
    increment: {
      onMouseDown: () => setIsIncrementActive(true),
      onMouseUp: () => setIsIncrementActive(false),
      onMouseLeave: () => {
        setIsIncrementActive(false)
        setIsIncrementHovered(false)
      },
      onMouseEnter: () => setIsIncrementHovered(true),
      onTouchStart: () => setIsIncrementActive(true),
      onTouchEnd: () => setIsIncrementActive(false),
    },
  }

  const { locale } = useLocale()
  const t = getTranslation(locale)

  return (
    <div className="flex items-center justify-between gap-1 rounded-full bg-[#F3F3F3] px-2 py-2">
      <button
        onClick={handleDecrement}
        disabled={value <= min}
        className="flex select-none items-center justify-center rounded-full p-[6px] text-base font-bold leading-none hover:bg-primary-500 hover:text-white hover:shadow disabled:cursor-not-allowed disabled:opacity-30"
        aria-label={t('common.decrement')}
        {...handleButtonMouseEvents.decrement}
      >
        <IcSubtract
          className="h-2"
          fill={isDecrementActive || isDecrementHovered ? '#fff' : '#000'}
        />
      </button>

      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        aria-label={t('common.quantity')}
        className="no-spinner mb-[1px] h-4 w-[20px] select-none rounded-sm border-none bg-transparent p-0 text-center text-[12px] text-base font-medium leading-4 outline-none focus:ring-1 focus:ring-transparent"
        style={{ touchAction: 'manipulation' }}
      />

      <button
        onClick={handleIncrement}
        disabled={value >= max}
        className="flex select-none items-center justify-center rounded-full p-[6px] text-base font-bold leading-none hover:bg-primary-500 hover:text-white hover:shadow disabled:cursor-not-allowed disabled:opacity-30"
        aria-label={t('common.increment')}
        {...handleButtonMouseEvents.increment}
      >
        <IcPlus fill={isIncrementActive || isIncrementHovered ? '#fff' : '#000'} />
      </button>
    </div>
  )
}
